import classnames from 'classnames'

import type { Step } from '@features/cpn.hooks/useWalletValidator'

// Component-specific constants and types
const WIZARD_STEPS = [
  { key: 'method' as const, label: 'Select Method' },
  { key: 'parameters' as const, label: 'Generate Payload' },
  { key: 'signature' as const, label: 'Validate Signature' },
  { key: 'results' as const, label: 'Results' },
] as const

interface StepIndicatorProps {
  currentStep: Step
}

/**
 * Get CSS classes for step indicator based on current state.
 */
const getStepIndicatorClass = (
  currentStep: Step,
  stepKey: string,
  index: number,
  currentStepIndex: number,
) => {
  const baseClasses =
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm type-body-sm-bold'

  if (currentStep === stepKey) {
    return `${baseClasses} bg-blue-600 text-white`
  }

  if (index < currentStepIndex) {
    return `${baseClasses} bg-green-600 text-white`
  }

  return `${baseClasses} bg-neutral-subtle text-neutral-subtle`
}

/**
 * Get the current step index from the step key.
 */
const getCurrentStepIndex = (currentStep: Step): number => {
  return WIZARD_STEPS.findIndex((step) => step.key === currentStep)
}

/**
 * Step indicator component for the wallet compatibility wizard
 * Shows progress through the 4-step validation process.
 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
}) => {
  const currentStepIndex = getCurrentStepIndex(currentStep)

  return (
    <div className="border-b border-neutral-subtle px-6 py-4">
      <div className="flex items-center justify-between">
        {WIZARD_STEPS.map((step, index) => (
          <div key={step.key} className="flex items-center">
            <div
              className={getStepIndicatorClass(
                currentStep,
                step.key,
                index,
                currentStepIndex,
              )}
            >
              {index + 1}
            </div>
            <span
              className={classnames(
                'ml-2 text-xs type-body-sm-bold sm:text-sm',
                currentStep === step.key
                  ? 'text-neutral-strong'
                  : 'text-neutral-subtle',
              )}
            >
              {step.label}
            </span>
            {index < WIZARD_STEPS.length - 1 && (
              <div className="mx-1 w-4 border-t border-neutral-subtle sm:mx-2 sm:w-8" />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
